/**
 * PIK Configuration Helper
 *
 * Centralizes all PIK-related display and behavior rules based on facility PIK option.
 * Used across loan creation, rollover, conversion, and merge forms to ensure consistent
 * PIK field display logic.
 *
 * @param pikOption - The facility's PIK option: 'PIK_All' | 'PIK_Optional' | 'PIK_Scheduled' | 'No_PIK'
 * @returns Configuration object with flags for controlling PIK UI elements
 */
export const getPikConfig = (pikOption?: string) => {
  const isPikAll = pikOption === 'PIK_All'
  const isPikOptional = pikOption === 'PIK_Optional'
  const isPikScheduled = pikOption === 'PIK_Scheduled'
  const isNoPik = pikOption === 'No_PIK' || !pikOption

  return {
    // === EXISTING LOAN PROPERTIES (rollover/conversion only) ===
    // Controls whether to show toggle for capitalizing accrued interest from existing loan
    showExistingLoanPikToggle: isPikOptional,
    // Default PIK percentage for capitalizing interest (100% for PIK_All, 0% otherwise)
    defaultPikPercentage: isPikAll ? 100 : 0,

    // === NEW LOAN PROPERTIES (all forms) ===
    // Controls whether to show toggle/select for enabling PIK on new loan (PIK_Optional only)
    showNewLoanPikToggle: isPikOptional,
    // Whether PIK should be auto-enabled for new loan (PIK_All/PIK_Scheduled)
    defaultNewLoanPikEnabled: isPikAll || isPikScheduled,

    // PIK configuration fields (Premium, Cash%, PIK%)
    // Always show for PIK_Scheduled regardless of toggle state
    showNewLoanPikFields: isPikScheduled,
    // Show for PIK_Optional when toggle/select is set to "Yes"/"On"
    showNewLoanPikFieldsWhenEnabled: isPikOptional,

    // === DISPLAY PROPERTIES ===
    // Controls whether to show entire PIK configuration section
    showPikConfiguration: !isNoPik,
    // Display label for PIK option type
    pikConfigLabel: isPikAll
      ? 'PIK All'
      : isPikOptional
        ? 'PIK Optional'
        : isPikScheduled
          ? 'PIK Scheduled'
          : 'N/A',

    // === HELPER FLAGS ===
    // Boolean flags for facility PIK option type
    isPikAll,
    isPikOptional,
    isPikScheduled,
    isNoPik,
  }
}

/**
 * Determines if PIK configuration fields (pikPremium, pikInterestPercent, cashInterestPercent)
 * should be saved as values or converted to null.
 *
 * Returns false (fields NOT applicable) for:
 * - PIK_All facilities (fields not shown, PIK is always 100%)
 * - No_PIK facilities (no PIK at all)
 * - PIK_Optional facilities with PIK disabled
 *
 * Returns true (fields ARE applicable) for:
 * - PIK_Scheduled facilities (fields always shown and applicable)
 * - PIK_Optional facilities with PIK enabled (fields shown when toggle is ON)
 *
 * @param pikOption - The facility's PIK option: 'PIK_All' | 'PIK_Optional' | 'PIK_Scheduled' | 'No_PIK'
 * @param isPikEnabled - Whether PIK is enabled (for PIK_Optional, this is the toggle state; for others, derived from pikOption)
 * @returns true if PIK fields should be saved as entered values, false if they should be converted to null
 */
export function arePikFieldsApplicable(
  pikOption: string | undefined,
  isPikEnabled: boolean,
): boolean {
  // PIK_All: Fields not shown, PIK happens automatically at 100%
  if (pikOption === 'PIK_All') {
    return false
  }

  // No_PIK: No PIK functionality
  if (pikOption === 'No_PIK' || !pikOption) {
    return false
  }

  // PIK_Optional: Fields applicable only when toggle is ON
  if (pikOption === 'PIK_Optional') {
    return isPikEnabled
  }

  // PIK_Scheduled: Fields always applicable
  return true
}
